// Form simples pra criar subscription owl_managed — Story 3.1.
// Wizard 3 passos é Story 3.2.

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createSubscriptionSchema,
  type BillingCycleSchema,
  type CreateSubscriptionDto,
  type CreateSubscriptionInput,
  type PaymentMethodSchema,
} from '@control-center/shared';

import { toast } from 'sonner';

import { Field, Input, Select, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import type { ClientEntity } from '../../lib/clients-api';
import { firstErrorMessage } from '../../lib/form-errors';
import type { PlanListItem } from '../../lib/plans-api';

export interface SubscriptionFormProps {
  clients: ClientEntity[];
  plans: PlanListItem[];
  isPending?: boolean;
  onSubmit: (values: CreateSubscriptionDto) => void | Promise<void>;
  onCancel?: () => void;
}

const BILLING_OPTIONS: { value: BillingCycleSchema; label: string }[] = [
  { value: 'mensal', label: 'Mensal' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
  { value: 'pagamento_unico', label: 'Pagamento único' },
];

// BOLETO fora da lista (regra do dono, 2026-08-19): boleto não debita sozinho,
// então assinatura nenhuma renova com ele. Cartão vem primeiro por ser o padrão
// — é o único meio que gera recorrência de verdade.
const PAYMENT_OPTIONS: { value: PaymentMethodSchema | ''; label: string }[] = [
  { value: '', label: '—' },
  { value: 'cartao', label: 'Cartão' },
  { value: 'pix', label: 'Pix' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'cobranca_manual', label: 'Cobrança manual' },
];

export function SubscriptionForm({
  clients,
  plans,
  isPending = false,
  onSubmit,
  onCancel,
}: SubscriptionFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateSubscriptionInput>({
    resolver: zodResolver(createSubscriptionSchema),
    defaultValues: {
      client_id: '',
      plan_id: '',
      start_date: new Date(),
      billing_cycle: 'mensal',
      payment_method: null,
      auto_renew: true,
      observations: '',
      sold_by_user_id: null,
      sale_origin: null,
    },
    mode: 'onBlur',
  });

  function submit(values: CreateSubscriptionInput): void | Promise<void> {
    const parsed = createSubscriptionSchema.parse(values);
    return onSubmit(parsed);
  }

  // Filtra clientes não cancelados + planos não descontinuados
  const eligibleClients = clients.filter((c) => c.status !== 'cancelado');
  const eligiblePlans = plans.filter((p) => p.status !== 'descontinuado');

  return (
    <form
      onSubmit={(e) =>
        void handleSubmit(submit, (errs) =>
          toast.error(firstErrorMessage(errs) ?? 'Formulário tem campos inválidos.'),
        )(e)
      }
      className="flex flex-col gap-4"
      noValidate
    >
      <Field label="Cliente" required error={errors.client_id?.message}>
        {({ inputId }) => (
          <Select id={inputId} {...register('client_id')}>
            <option value="" disabled>
              Selecione…
            </option>
            {eligibleClients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.document ? `(${c.document})` : ''}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Plano" required error={errors.plan_id?.message}>
        {({ inputId }) => (
          <Select id={inputId} {...register('plan_id')}>
            <option value="" disabled>
              Selecione…
            </option>
            {eligiblePlans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.type})
              </option>
            ))}
          </Select>
        )}
      </Field>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Data de início" required error={errors.start_date?.message}>
          {({ inputId }) => (
            <Input id={inputId} type="date" {...register('start_date', { valueAsDate: true })} />
          )}
        </Field>

        <Field label="Ciclo de cobrança" required error={errors.billing_cycle?.message}>
          {({ inputId }) => (
            <Select id={inputId} {...register('billing_cycle')}>
              {BILLING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Forma de pagamento">
          {({ inputId }) => (
            <Select
              id={inputId}
              {...register('payment_method', {
                setValueAs: (v) => (v === '' ? null : v),
              })}
            >
              {PAYMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <div className="flex flex-col gap-2 pt-6 text-[13px]">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-line"
              defaultChecked
              {...register('auto_renew')}
            />
            <span>Renovação automática</span>
          </label>
        </div>
      </div>

      <Field label="Observações">
        {({ inputId }) => (
          <Textarea
            id={inputId}
            rows={2}
            placeholder="Notas internas sobre essa venda…"
            {...register('observations')}
          />
        )}
      </Field>

      <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
        {onCancel && (
          <PillButton onClick={onCancel} type="button" disabled={isPending}>
            Cancelar
          </PillButton>
        )}
        <PillButton solid type="submit" disabled={isPending}>
          {isPending ? 'Criando…' : 'Criar assinatura'}
        </PillButton>
      </div>
    </form>
  );
}
